/**
 * Color Picker Component
 *
 * Simple but powerful color picker with curated palettes.
 * Instead of overwhelming users with RGB sliders, we provide:
 * - Curated palette of 40+ beautiful colors
 * - Organized by color family
 * - Recent colors history
 * - Optional contrast checker for accessibility
 * - Custom HEX input for advanced users
 */

import { useState, useEffect } from 'react';
import { Check, Palette, History } from 'lucide-react';
import { cn } from '@/client/lib/utils';
import { Input } from '@/client/components/ui/input';

// Color palettes and utilities implementation
const COLOR_PALETTES = [
  {
    name: 'Neutrals',
    colors: ['#ffffff', '#f9fafb', '#f3f4f6', '#e5e7eb', '#d1d5db', '#9ca3af', '#6b7280', '#4b5563', '#374151', '#1f2937', '#111827', '#000000'],
  },
  {
    name: 'Blues',
    colors: ['#eff6ff', '#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#1e3a8a'],
  },
  {
    name: 'Greens',
    colors: ['#f0fdf4', '#dcfce7', '#bbf7d0', '#86efac', '#4ade80', '#22c55e', '#16a34a', '#15803d', '#166534', '#14532d'],
  },
  {
    name: 'Emeralds',
    colors: ['#ecfdf5', '#d1fae5', '#a7f3d0', '#6ee7b7', '#34d399', '#10b981', '#059669', '#047857', '#065f46', '#064e3b'],
  },
  {
    name: 'Purples',
    colors: ['#faf5ff', '#f3e8ff', '#e9d5ff', '#d8b4fe', '#c084fc', '#a855f7', '#9333ea', '#7e22ce', '#6b21a8', '#581c87'],
  },
  {
    name: 'Pinks',
    colors: ['#fdf2f8', '#fce7f3', '#fbcfe8', '#f9a8d4', '#f472b6', '#ec4899', '#db2777', '#be185d', '#9d174d', '#831843'],
  },
  {
    name: 'Reds',
    colors: ['#fef2f2', '#fee2e2', '#fecaca', '#fca5a5', '#f87171', '#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d'],
  },
  {
    name: 'Oranges',
    colors: ['#fff7ed', '#ffedd5', '#fed7aa', '#fdba74', '#fb923c', '#f97316', '#ea580c', '#c2410c', '#9a3412', '#7c2d12'],
  },
  {
    name: 'Ambers',
    colors: ['#fffbeb', '#fef3c7', '#fde68a', '#fcd34d', '#fbbf24', '#f59e0b', '#d97706', '#b45309', '#92400e', '#78350f'],
  },
];

const STORAGE_KEY = 'arml_recent_colors';

function getRecentColors(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function addRecentColor(color: string): void {
  const recent = getRecentColors().filter((c) => c.toLowerCase() !== color.toLowerCase());
  localStorage.setItem(STORAGE_KEY, JSON.stringify([color, ...recent].slice(0, 8)));
}

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
}

export function ColorPicker({ value, onChange, label }: ColorPickerProps) {
  const [recentColors, setRecentColors] = useState<string[]>([]);
  const [customHex, setCustomHex] = useState(value);

  useEffect(() => setRecentColors(getRecentColors()), []);
  useEffect(() => setCustomHex(value), [value]);

  const handleColorSelect = (color: string) => {
    onChange(color);
    addRecentColor(color);
    setRecentColors(getRecentColors());
  };

  return (
    <div className="space-y-4">
      {label && <label className="block text-sm font-medium">{label}</label>}

      {recentColors.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <History className="h-3 w-3" />
            <span>Recent</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {recentColors.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => handleColorSelect(color)}
                className={cn(
                  'w-10 h-10 rounded-lg border-2 transition-all hover:scale-110',
                  value.toLowerCase() === color.toLowerCase() ? 'ring-2 ring-primary ring-offset-2 scale-110' : 'border-border'
                )}
                style={{ backgroundColor: color }}
              >
                {value.toLowerCase() === color.toLowerCase() && <Check className="h-4 w-4 text-white mx-auto" />}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-lg border-2 border-border" style={{ backgroundColor: value }} />
        <Input
          type="text"
          value={customHex}
          onChange={(e) => {
            setCustomHex(e.target.value);
            if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) handleColorSelect(e.target.value);
          }}
          placeholder="#000000"
          className="font-mono text-sm uppercase"
          maxLength={7}
        />
      </div>

      <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
        {COLOR_PALETTES.map((palette) => (
          <div key={palette.name} className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Palette className="h-3 w-3" />
              <span className="font-medium">{palette.name}</span>
            </div>
            <div className="grid grid-cols-6 gap-2">
              {palette.colors.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleColorSelect(color)}
                  className={cn(
                    'w-full aspect-square rounded-lg border-2 transition-all hover:scale-110',
                    value.toLowerCase() === color.toLowerCase() ? 'ring-2 ring-primary ring-offset-2 scale-110' : 'border-border'
                  )}
                  style={{ backgroundColor: color }}
                >
                  {value.toLowerCase() === color.toLowerCase() && <Check className="h-4 w-4 text-white mx-auto" />}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
